/**
 * Apply the account language on the pre-auth 2FA / MFA challenge screen.
 *
 * The challenge card renders AFTER the backend knows who the user is but BEFORE
 * a JWT is issued, so {@link useApplyUserLanguage} (which only runs once
 * `isAuthenticated`) can't reconcile the UI language yet. On a fresh browser
 * there is also no cross-subdomain known-language cookie to fall back on (that
 * only exists once the user has signed in here before), so the challenge would
 * otherwise stay in the pre-auth GeoIP/browser guess.
 *
 * The backend therefore carries the known user's `preferred_language` on the
 * challenge payload (see `_evaluate_mfa_gate`). This hook switches the UI into
 * it — UNLESS the user made an explicit LanguageSelector choice on the login
 * screen (LANGUAGE_EXPLICIT_KEY), which is a deliberate override that must win
 * and is later adopted into the account by {@link useApplyUserLanguage}.
 *
 * Mount it in the challenge component (see MfaChallengeStep) — it is a no-op
 * whenever the language is absent, unsupported, or already active.
 */
import { useEffect } from 'react';

import type { SupportedLanguage } from '../shared/auth/types';
import i18n, { SUPPORTED_LANGUAGES, changeLanguage, persistKnownLanguage } from './config';
import { LANGUAGE_EXPLICIT_KEY } from './useGeoLanguageDefault';

function isSupported(lang: string | null | undefined): lang is SupportedLanguage {
  return !!lang && SUPPORTED_LANGUAGES.some((l) => l.code === lang);
}

export function useApplyChallengeLanguage(preferredLanguage: string | null | undefined): void {
  useEffect(() => {
    if (!isSupported(preferredLanguage)) return;
    // A deliberate login-screen choice wins over the account preference.
    if (typeof localStorage !== 'undefined' && localStorage.getItem(LANGUAGE_EXPLICIT_KEY)) return;
    // Record the known preference cross-subdomain so a reload of the challenge
    // host keeps it, then switch the UI (skip if already active — compare base
    // codes, "en-US" -> "en").
    persistKnownLanguage(preferredLanguage);
    if (i18n.language?.split('-')[0] === preferredLanguage) return;
    void changeLanguage(preferredLanguage);
  }, [preferredLanguage]);
}
